import { api, request, setSession, type Result, type Session } from './client'
import type {
  DroneFeed,
  DroneWatch,
  Message,
  MessageScope,
  MapFeature,
  MapLayer,
  Mission,
  MissionRole,
  MissionTeam,
  Organisation,
  OrganisationMember,
  OrganisationRole,
  Participant,
  Position,
  ServerInfo,
  TacticalReport,
  User,
} from './types'

/** Confirm an address is a TacMaps server before anything is saved against it. */
export const probeServer = () => request<ServerInfo>('/api/v1/server', { anonymous: true })

export async function signIn(email: string, password: string): Promise<Result<User>> {
  const result = await request<Session & { user: User }>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password },
    anonymous: true,
  })
  if (!result.ok) return result

  setSession(result.value)
  return { ok: true, value: result.value.user }
}

/**
 * Create an account and sign in with it, in one round trip.
 *
 * The server returns the same payload as signing in, so there is no second request and no window
 * where an account exists that nobody is holding a session for.
 *
 * Worth knowing on a fresh self-hosted instance: the first account to register becomes the
 * instance administrator, and is the only one that can create organisations. Everything else --
 * events, join codes, teams -- hangs off an organisation, so whoever does this first decides who
 * runs the server.
 */
export async function register(details: {
  email: string
  password: string
  callsign: string
  fullName?: string
}): Promise<Result<User>> {
  const result = await request<Session & { user: User }>('/api/v1/auth/register', {
    method: 'POST',
    body: details,
    anonymous: true,
  })
  if (!result.ok) return result

  setSession(result.value)
  return { ok: true, value: result.value.user }
}

export function signOut() {
  setSession(null)
}

export const me = () => api.get<User>('/api/v1/users/me')

// ---------------------------------------------------------------- organisations

export const myOrganisations = () => api.get<Organisation[]>('/api/v1/organisations')

export const allOrganisations = () =>
  api.get<{ items: Organisation[] }>('/api/v1/organisations/all')

export const getOrganisation = (id: string) => api.get<Organisation>(`/api/v1/organisations/${id}`)

export const createOrganisation = (body: {
  name: string
  description?: string
  contact?: string
  ownerUserId?: string
}) => api.post<Organisation>('/api/v1/organisations', body)

export const organisationMembers = (id: string) =>
  api.get<OrganisationMember[]>(`/api/v1/organisations/${id}/members`)

/**
 * Add somebody, or change what they may do.
 *
 * Named by the email they registered with, because that is how one person knows another — nobody
 * knows anybody else's user id. The server resolves it, so there is no endpoint for turning an
 * address into an account, and therefore no way to ask which addresses have one.
 */
export const addOrganisationMember = (
  id: string,
  who: { email: string } | { userId: string },
  role: OrganisationRole,
) => api.post<OrganisationMember>(`/api/v1/organisations/${id}/members`, { ...who, role })

export const removeOrganisationMember = (id: string, userId: string) =>
  api.delete<void>(`/api/v1/organisations/${id}/members/${userId}`)

// ---------------------------------------------------------------------- events

export const organisationMissions = (organisationId: string) =>
  api.get<Mission[]>(`/api/v1/organisations/${organisationId}/missions`)

export const createMission = (
  organisationId: string,
  body: {
    name: string
    description?: string
    locationName?: string
    startsAt?: string
    centreLatitude?: number
    centreLongitude?: number
    hostTeamId?: string
    factions?: { key: string; name: string; displayOrder: number }[]
  },
) => api.post<Mission>(`/api/v1/organisations/${organisationId}/missions`, body)

export const getMission = (id: string) => api.get<Mission>(`/api/v1/missions/${id}`)

export const updateMission = (id: string, body: Record<string, unknown>) =>
  api.patch<Mission>(`/api/v1/missions/${id}`, body)

export const missionTeams = (id: string) => api.get<MissionTeam[]>(`/api/v1/missions/${id}/teams`)

export const missionRoster = (id: string) =>
  api.get<Participant[]>(`/api/v1/missions/${id}/participants`)

export const missionPositions = (id: string) =>
  api.get<Position[]>(`/api/v1/missions/${id}/positions`)

export const missionFeatures = (id: string) =>
  api.get<MapFeature[]>(`/api/v1/missions/${id}/features`)

export const missionReports = (id: string) =>
  api.get<TacticalReport[]>(`/api/v1/missions/${id}/reports`)

/**
 * Place an objective.
 *
 * Mission-wide by default. An objective everybody is fighting over that only one side can see is
 * not an objective, it is an ambush.
 */
export const createObjective = (
  missionId: string,
  body: {
    latitude: number
    longitude: number
    label: string
    remarks?: string
  },
) =>
  api.post<MapFeature>(`/api/v1/missions/${missionId}/features`, {
    geometryType: 'POINT',
    positions: [{ latitude: body.latitude, longitude: body.longitude }],
    symbol: 'OBJECTIVE',
    affiliation: 'NEUTRAL',
    label: body.label,
    remarks: body.remarks,
    objective: true,
    visibility: 'MISSION',
  })

/** Draw something on the map. A host may name the side it is for with `factionKey`. */
export const createFeature = (missionId: string, body: Record<string, unknown>) =>
  api.post<MapFeature>(`/api/v1/missions/${missionId}/features`, body)

/** Move a report along: acknowledged, in progress, complete. */
export const setReportStatus = (reportId: string, status: TacticalReport['status']) =>
  api.patch<TacticalReport>(`/api/v1/reports/${reportId}/status`, { status })

/** Close a report as no longer needed; it stays in the log. */
export const withdrawReport = (reportId: string) => api.delete<void>(`/api/v1/reports/${reportId}`)

/** Remove a report from the record, with its marker. Host only. */
export const deleteReport = (reportId: string) =>
  api.delete<void>(`/api/v1/reports/${reportId}?permanent=true`)

/** Remove a message: its sender, or a host. */
export const deleteMessage = (missionId: string, messageId: string) =>
  api.delete<void>(`/api/v1/missions/${missionId}/messages/${messageId}`)

/** Everything the caller can see in the event, as KML. */
export const exportKml = (missionId: string) =>
  api.text(`/api/v1/missions/${missionId}/features/export.kml`)

/** Many features at once, for an imported file. All or nothing on the server. */
export const createFeatures = (missionId: string, features: Record<string, unknown>[]) =>
  api.post<MapFeature[]>(`/api/v1/missions/${missionId}/features/batch`, { features })

/** Replace a feature. The server takes the whole thing, so build the body with `saveBody`. */
export const updateFeature = (featureId: string, body: Record<string, unknown>) =>
  request<MapFeature>(`/api/v1/features/${featureId}`, { method: 'PUT', body })

export const deleteFeature = (featureId: string) =>
  api.delete<void>(`/api/v1/features/${featureId}`)

export const moveTeamToFaction = (missionId: string, teamId: string, factionKey: string) =>
  api.patch<void>(`/api/v1/missions/${missionId}/teams/${teamId}`, { factionKey })

/**
 * Make somebody a host, marshal or player. Host only.
 *
 * An event can have several hosts with equal powers; the server refuses to remove the last one.
 */
export const setMissionRole = (missionId: string, userId: string, role: MissionRole) =>
  api.patch<void>(`/api/v1/missions/${missionId}/participants/${userId}/role`, { role })

/**
 * Recent messages on one channel, oldest first.
 *
 * `with` is the other party and applies to DIRECT only. There is deliberately no "everything"
 * fetch: the scopes carry different audiences, and one endpoint returning a mixture would move the
 * decision about who may read what into whatever merged the list.
 */
export const missionMessages = (
  missionId: string,
  scope: MessageScope,
  withUserId?: string,
  limit = 50,
) =>
  api.get<Message[]>(
    `/api/v1/missions/${missionId}/messages?scope=${scope}` +
      (withUserId ? `&with=${encodeURIComponent(withUserId)}` : '') +
      `&limit=${limit}`,
  )

/**
 * Send to a channel.
 *
 * `clientMessageId` makes a retry safe: the server returns the original when the same id arrives
 * twice, so a dashboard that resends after a dropped request does not post twice.
 */
export const sendMissionMessage = (
  missionId: string,
  body: { scope: MessageScope; body: string; recipientId?: string; clientMessageId?: string },
) => api.post<Message>(`/api/v1/missions/${missionId}/messages`, body)

export const layers = () => api.get<MapLayer[]>('/api/v1/layers')

/** The event's drone feeds and which are on the air. Hosts only. */
export const droneFeeds = (missionId: string) =>
  api.get<DroneFeed[]>(`/api/v1/missions/${missionId}/drone-feeds`)

export const createDroneFeed = (missionId: string, name: string) =>
  api.post<DroneFeed>(`/api/v1/missions/${missionId}/drone-feeds`, { name })

/** A new stream key; the drone has to be set up again with it. */
export const rotateDroneFeedKey = (missionId: string, feedId: string) =>
  api.post<DroneFeed>(`/api/v1/missions/${missionId}/drone-feeds/${feedId}/rotate-key`)

export const deleteDroneFeed = (missionId: string, feedId: string) =>
  api.delete<void>(`/api/v1/missions/${missionId}/drone-feeds/${feedId}`)

/** A pass to watch one feed: a WHEP address that works for a couple of minutes. */
export const watchDroneFeed = (missionId: string, feedId: string) =>
  api.post<DroneWatch>(`/api/v1/missions/${missionId}/drone-feeds/${feedId}/watch`)
